<template>
    <div><h1 class="wrapper">Start verification</h1>
    <div class="email-validation-container">
        <div class="pre-requisites-container pt-0">
            <h2 class="header">Let's get started</h2>
            <h2 class="sub-header pt-0">Be prepared with:</h2>

            <ul class="pre-requisite-list">
                <li class="desktop-view">
                    <div class="list">1.</div>
                    <span class="pre-requisite-details">A personal mobile device</span>
                </li>
                <li>
                    <div class="list desktop-view">2.</div>
                    <div class="list mobile-view">1.</div>
                    <span class="pre-requisite-details">A valid <a class="link"
                            href="https://getnametag.com/docs/govtid-doctypes/" target="_blank">government-issued photo ID</a>
                        (originals only)</span>
                </li>
                <li>
                    <div class="list desktop-view">3.</div>
                    <div class="list mobile-view">2.</div>
                    <span class="pre-requisite-details">A stable internet connection</span>
                </li>
            </ul>
        </div>
        <div class="email-container">
            <label for="email" class="email-label">Enter your Autodesk email address</label>
            <input type="email" id="email" v-model="email" class="email-input" placeholder="mailid@autodesk.com"
                @input="validateEmail" :style="{border: emailError || !isAllowedToUseAutomation ? '1px solid red' : '1px solid #ccc'}" />
            <div v-if="emailError || !isAllowedToUseAutomation" class="d-flex flex-row justify-content-start align-items-start mt-1">
                <img v-if="!isAllowedToUseAutomation" class="error-image mt-1" src="../assets/images/error_icon.png" alt="Error Image" />
                <span v-if="emailError || !isAllowedToUseAutomation" class="error-message" v-html="errorMessage"></span>
            </div>

            <div class="checkbox-container mb-0">
                <input type="checkbox" id="consent" v-model="consent" class="custom-checkbox"
                    @change="isConsentGiven" />
                <div class="checkbox-label text-justify ml-1" v-html="userDataUsageConsent"></div>
            </div>

            <div class="d-flex justify-content-end button-container">
                <button class="btn flat-btn blue-btn mt-3 mr-2 cancel-button cursor-pointer p-0" @click="cancelStep">Cancel</button>
                <button class="btn flat-btn primary-blue-btn next-button mt-3 cursor-pointer p-0" :disabled="!isEmailAndConsentValid || !isAllowedToUseAutomation"
                    @click="nextStep">Next</button>
            </div>
            <loading-overlay v-if="isLoading"></loading-overlay>
        </div>
    </div>
    </div>
</template>

<script>
import LoadingOverlay from "@/components/LoadingOverlay.vue";
import { createNametagVerificationRequest } from '@/api/index.js';
import CONSTANTS from "@/constants";

export default {
    name: "BeginIdentityVerification",
    components: {
        LoadingOverlay
    },
    props: {
        userDataUsageConsent: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            email: "",
            consent: false,
            emailError: false,
            errorMessage: "Please provide a valid Autodesk email address",
            isAllowedToUseAutomation: true,
            isEmailAndConsentValid: false,
            isLoading: false
        };
    },
    methods: {
        validateEmail() {
            const regex = /^[a-zA-Z0-9._%+-]+@autodesk\.com$/;
            this.emailError = !regex.test(this.email);
            this.errorMessage= this.emailError ? "Please provide a valid Autodesk email address" : "";
            this.isAllowedToUseAutomation = true;
            this.isConsentGiven();
        },
        cancelStep() {
            document.title = 'Enterprise Search';
            this.$router.push('/');
        },
        isConsentGiven() {
            this.isEmailAndConsentValid = !this.emailError && this.consent && this.email.length > 0;
        },
        handleVerificationAllowance(response) {
            const message = JSON.parse(response.body)
            this.isAllowedToUseAutomation = false;
            if (message.message.includes("Max retry")) {
                this.errorMessage = response.is_contingent ? CONSTANTS.ERROR_MESSAGES.Retry_Limit_Exceeded_Contingents :CONSTANTS.ERROR_MESSAGES.Retry_Limit_Exceeded;
            }
            else if (response.is_restricted_region) {
                this.errorMessage = response.is_contingent ? CONSTANTS.ERROR_MESSAGES.isRestrictedRegionContingent :CONSTANTS.ERROR_MESSAGES.isRestrictedRegion;
            } else if (!response.is_new_hire) {
                this.errorMessage = response.is_contingent ? CONSTANTS.ERROR_MESSAGES.isContingent :CONSTANTS.ERROR_MESSAGES.isNewHire;
            } else if (message.message.includes("24 hrs")) {
                this.errorMessage = CONSTANTS.ERROR_MESSAGES.verifiedIn24hrs;
            } else {
                this.isAllowedToUseAutomation = true;
                return true;
            }
            return false;
        },
        async nextStep() {
            if (this.isEmailAndConsentValid) {
                this.isLoading = true;
                try {
                    const response = await createNametagVerificationRequest(this.email);
                    const data = await response.json();
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                    const isVerificationAllowed = this.handleVerificationAllowance(data);
                    isVerificationAllowed ? this.$emit("nextStep", data, this.email) : this.consent = false;
                } catch (error) {
                    this.emailError = "Server Error!! Please contact Service Desk for more details"
                } finally {
                    this.isLoading = false;
                }
            }
        },
    }
};
</script>

<style scoped>
.wrapper {
    font-size: 20px;
    font-weight: bolder;
    color: black;
    font-family: sans-serif;
    margin: 0px;
    margin-top: 40px;
    padding-left: 10px;
}
.cancel-button:hover {
    background-color: transparent !important;
    color: inherit !important;
}
.email-validation-container {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
    width: 100%;
    height: auto;
    max-width: 1200px;
    color: black;
}

.error-image {
    width: 20px;
    height: 20px;
    margin-right: 5px;
}

.pre-requisites-container {
    width: 50%;
    height: 100%;
    padding: 30px 10px;
}

.email-container {
    width: 50%;
    height: 100%;
    padding: 20px 30px;
    border-left: 1px solid gray;
}

.header {
    font-size: 18px;
    font-weight: 700;
    color: black;
    font-family: artifaktElementBold;
    margin: 0px;
    margin-bottom: 14px;
    padding-top: 20px;
}

.sub-header {
    font-size: 16px;
    font-weight: normal;
    color: black;
    font-family: artifaktElementRegular;
    margin: 0px;
    margin-bottom: 20px;
}

.mobile-view {
    display: none !important;
}

.desktop-view {
    display: flex !important;
}

.list {
    width: 18px;
    height: 18px;
    font-size: 16px;
    text-align: center;
    font-family: artifaktElementRegular;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    margin-top: 3px !important;
}

.custom-checkbox {
    height: 18px;
    width: 18px;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 3px;
    margin-right: 8px;
    cursor: pointer;
    margin-top: 4px;
    position: relative;
}

.custom-checkbox:checked {
    background-color: var(--secondary-color);
    border-color: var(--secondary-color);
}

.custom-checkbox:checked::before {
    content: '';
    position: absolute;
    width: 6px;
    height: 13px;
    border-right: 3px solid white;
    border-bottom: 3px solid white;
    top: 1px;
    left: 6px;
    transform: rotate(40deg);
}

.pre-requisite-list {
    list-style-type: none;
    padding: 0;
    margin: 0;
}

.pre-requisite-list li {
    display: flex;
    align-items: flex-start;
    margin-bottom: 5px;
    text-align: center;
}

.icon {
    margin-right: 10px;
    width: 20px;
    height: 20px;
}

.pre-requisite-details {
    text-align: left;
    font-size: 16px;
    font-weight: normal;
    color: black;
    font-family: artifaktElementRegular;
    width: 80%;
}

.email-label {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 8px;
    color: black;
    font-family: artifaktElementBold;
}

.email-input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ccc;
    font-size: 14px;
}

.error-message {
    color: red;
    font-size: 12px;
}

.button {
    border-radius: 40px;
    font-weight: 700;
    font-size: 14px;
    width: 130px;
    height: 36px;
}

.cancel-button {
    background-color: white;
    min-width: 80px !important;
    height: 40px;
    text-transform: capitalize;
    border-radius: 4px !important;
    font-weight: bolder;
    max-width: 190px;
}

.next-button {
    text-transform: capitalize;
    min-width: 80px !important;
    height: 40px;
    color: white;
    border: none;
    border-radius: 4px !important;
    font-weight: bolder;
    max-width: 190px;
}

.btn:disabled {
    opacity: 0.35 !important;
}

.button-container {
    margin-top: 20px;
}

.next-button:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.checkbox-container {
    display: flex;
    margin: 20px 0px;
}

.checkbox-label {
    font-size: 14px;
    width: 90%;
}

.link {
    color: #000000 !important;
    word-break: break-all;
    text-decoration: underline;
}

.error-link {
    color: red !important;
    text-decoration: underline;
}

@media (max-width: 700px) {
    .email-validation-container {
        flex-direction: column;
    }

    .pre-requisites-container {
        width: 100%;
        padding: 0px;
        padding-bottom: 10px;
        border-bottom: 1px solid gray;
    }

    .pre-requisite-details {
        text-align: start;
    }

    .email-container {
        width: 100%;
        padding: 0px;
        border-left: none;
    }

    .mobile-view {
        display: flex !important;
    }

    .desktop-view {
        display: none !important;
    }

    .email-label {
        font-size: 16px;
        margin-top: 20px;
    }

    .header {
        font-size: 16px;
    }

    .pre-requisite-list li {
        align-items: flex-start;
        text-align: start;
    }

    .button-container {
        justify-content: center !important;
    }

    .wrapper {
        padding: 0px;
        margin-top: 20px;
        font-size: 18px;
    }
}
</style>